import React from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Post } from '../data/Post';
import { Category } from '../data/Category';

import PostContent from '../components/PostContent';
import btnPrev from '../img/btn-arrow-left.png';
import btnNext from '../img/btn-arrow-right.png';

import './MultiPostPage.scss';
import { connect } from 'react-redux';
import { State } from '../data/rootReducer';
import { AppConnectedProps } from '../data/store';
import { fetchPost, fetchPosts, getPost, getPostNeighbors } from '../data/postsSlice';
import { Lazy } from '../data/lazy';
import { Header } from '../components/Header';

const STICKY_THRESHOLD = 75;

const connector = connect((state: State) => ({
  posts: state.posts,
}));

type MultiPostProps = AppConnectedProps<typeof connector> &
  RouteComponentProps<{}> & {
    category: Category;
    slug: string;
  };

class MultiPostPage extends React.Component<MultiPostProps> {
  private draggable: HTMLDivElement | null = null;
  private dragStatus: 'still' | 'dragging' | 'transition' = 'still';
  private horizontalDrag: number = 0;
  private dragStart: { x: number; y: number } | null = null;
  private keyEventHandler?: (e: KeyboardEvent) => void = null;

  componentDidMount() {
    this.props.dispatch(fetchPost(this.props.category, this.props.slug));
    this.props.dispatch(fetchPosts(this.props.category));

    this.keyEventHandler = (e: KeyboardEvent) => this.handleKeyEvent(e);
    document.body.addEventListener('keydown', this.keyEventHandler);

    if (this.props.category === 'sketches') {
      document.body.classList.add('lighter');
    }
    document.body.classList.add('full-height');
  }

  componentWillUnmount() {
    document.body.classList.remove('full-height');
    if (this.props.category === 'sketches') {
      document.body.classList.remove('lighter');
    }

    document.body.removeEventListener('keydown', this.keyEventHandler);
    this.keyEventHandler = null;
  }

  render(): JSX.Element {
    const post = getPost(this.props.posts, this.props.category, this.props.slug);
    const { prev, next, index, total } = getPostNeighbors(
      this.props.posts,
      this.props.category,
      this.props.slug
    );

    if (post.status === 'dead') {
      this.props.history.push('/404');
      return <></>;
    }

    return (
      <>
        {post.status === 'awake' ? (
          <Helmet>
            <meta name="theme-color" content="#faf4ef" />
            <title>{post.item.title || 'Untitled'} - Who Is NemO'S?</title>
            {/* TODO: Add something on all other pages */}
            <meta property="og:image" content={post.item.thumbnail} />
          </Helmet>
        ) : (
          ''
        )}
        <div className="page-content multi-page">
          <Header backLink={'/' + this.props.category} page={this.props.category} />
          {this.renderNavBar(prev, next, index, total)}
          <div
            className="post-container"
            onTouchStart={(e) => this.touchStart(e)}
            onTouchMove={(e) => this.touchMove(e)}
            onTouchEnd={(e) => this.touchEnd(e, prev, next)}
          >
            <div className="draggable" ref={(draggable) => (this.draggable = draggable)}>
              {prev.status === 'awake' ? (
                <PostContent
                  className="prev"
                  category={this.props.category}
                  slug={prev.item.slug}
                />
              ) : (
                ''
              )}
              <PostContent
                className="current"
                category={this.props.category}
                slug={this.props.slug}
              />
              {next.status === 'awake' ? (
                <PostContent
                  className="next"
                  category={this.props.category}
                  slug={next.item.slug}
                />
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  renderNavBar(
    prev: Lazy<Post>,
    next: Lazy<Post>,
    index: number | null,
    total: number | null
  ): JSX.Element {
    return (
      <div className="nav-buttons">
        {this.renderPrevLink(prev)}
        {index != null && total != null ? (
          <span className="post-index">
            {index + 1}/{total}
          </span>
        ) : null}
        {this.renderNextLink(next)}
      </div>
    );
  }

  private renderPrevLink(prev: Lazy<Post> = this.getPrevPost()): JSX.Element {
    if (prev.status !== 'awake') {
      return null;
    }
    const prevLink = '/' + this.props.category + '/' + prev.item.slug;
    return (
      <Link className="button prev" to={prevLink}>
        <img src={btnPrev} alt="Previous" />
      </Link>
    );
  }

  private renderNextLink(next: Lazy<Post> = this.getNextPost()): JSX.Element {
    if (next.status !== 'awake') {
      return null;
    }
    const nextLink = '/' + this.props.category + '/' + next.item.slug;
    return (
      <Link className="button next" to={nextLink}>
        <img src={btnNext} alt="Next" />
      </Link>
    );
  }

  private navigateToPrev(prev: Lazy<Post> = this.getPrevPost()) {
    if (prev.status !== 'awake') {
      return;
    }
    const prevLink = '/' + this.props.category + '/' + prev.item.slug;
    this.props.history.push(prevLink);
  }

  private navigateToNext(next: Lazy<Post> = this.getNextPost()) {
    if (next.status !== 'awake') {
      return;
    }
    const nextLink = '/' + this.props.category + '/' + next.item.slug;
    this.props.history.push(nextLink);
  }

  private getPrevPost(): Lazy<Post> {
    return getPostNeighbors(this.props.posts, this.props.category, this.props.slug).prev;
  }

  private getNextPost(): Lazy<Post> {
    return getPostNeighbors(this.props.posts, this.props.category, this.props.slug).next;
  }

  private touchStart(e: React.TouchEvent<HTMLDivElement>) {
    if (this.dragStatus !== 'still') {
      return;
    }
    this.dragStart = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };
    this.dragStatus = 'dragging';
    this.draggable.classList.add('dragging');
  }

  private handleKeyEvent(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowLeft':
        this.navigateToPrev();
        break;
      case 'ArrowRight':
        this.navigateToNext();
        break;
    }
  }

  private touchMove(e: React.TouchEvent<HTMLDivElement>) {
    const horizontalDrag = e.changedTouches[0].clientX - this.dragStart.x;
    const verticalDrag = e.changedTouches[0].clientY - this.dragStart.y;
    if (Math.abs(verticalDrag) > Math.abs(horizontalDrag)) {
      // Treat as vertical scroll, reset dragOffset.
      this.draggable.style.setProperty('--dragOffset', '0px');
      return;
    }
    if (e.cancelable) {
      // Treat as carousel scroll, don't tell the browser about this event.
      e.preventDefault();
    }

    this.horizontalDrag = horizontalDrag;
    this.draggable.style.setProperty('--dragOffset', horizontalDrag * 2 + 'px');
  }

  private touchEnd(e: React.TouchEvent<HTMLDivElement>, prev: Lazy<Post>, next: Lazy<Post>) {
    let dragOffset = '0px';
    if (Math.abs(this.horizontalDrag) > STICKY_THRESHOLD) {
      document.body.scrollTop = 0; // Scroll up.
      let navigationFunction: Function | null = null;
      if (this.horizontalDrag > 0 && prev) {
        navigationFunction = () => this.navigateToPrev(prev);
        dragOffset = '100%';
      } else if (this.horizontalDrag < 0 && next) {
        navigationFunction = () => this.navigateToNext(next);
        dragOffset = '-100%';
      }
      if (navigationFunction !== null) {
        this.dragStatus = 'transition';
        const handler = () => {
          this.draggable.removeEventListener('transitionend', handler);
          this.dragStatus = 'still';
          navigationFunction();
        };
        this.draggable.addEventListener('transitionend', handler);
      } else {
        this.dragStatus = 'still';
      }
    } else {
      this.dragStatus = 'still';
    }
    this.horizontalDrag = 0;
    this.draggable.classList.remove('dragging');
    this.draggable.style.setProperty('--dragOffset', dragOffset);
  }
}

export default withRouter(connector(MultiPostPage));
